import { css } from '@emotion/core';
import React from 'react';

import { MultiSelectField, RichTextField, SelectField, TextField } from '../core-ui/form-fields';
import { JobFormData } from '../graphql/queries/job-form';
import { useFormContext } from './form-context';
import { JobOnePager } from './job-one-pager';

const locationRowStyles = css`
    display: flex;
    gap: 1rem;

    .job-form-location {
        flex: 1 1 auto;
    }

    .job-form-timezone {
        flex: 0 0 auto;
        width: 160px;
    }
`;

export const JobFormJD: React.FC = () => {
    const {
        data: { description, id },
        onFieldChange,
        disabled
    } = useFormContext<JobFormData>();

    const handleFieldChange =
        <T extends keyof JobFormData['description']>(field: T) =>
        (value: JobFormData['description'][T]) => {
            onFieldChange('description')({ ...description, [field]: value });
        };

    const visaOptions = [
        { label: 'No Visa Sponsorship', value: 'no_visa' },
        { label: 'Visa Transfers OK', value: 'h1_transfers' },
        { label: 'TN Visa OK', value: 'tn_visa' },
        { label: 'New Visa Sponsorship OK', value: 'h1_sponsorships' }
    ];

    const timezoneOptions = [
        { label: 'Pacific', value: 'America/Los_Angeles' },
        { label: 'Mountain', value: 'America/Denver' },
        { label: 'Central', value: 'America/Chicago' },
        { label: 'Eastern', value: 'America/New_York' },
        { label: 'Hawaii', value: 'Pacific/Honolulu' }
    ];

    const diversityOptions = [
        { label: 'Diversity Only', value: 'diversity_only' },
        { label: 'Diversity Preferred', value: 'diversity_preferred' },
        { label: 'Not Relevant', value: 'not_relevant' }
    ];

    return (
        <div>
            <div className="job-field-row">
                <RichTextField
                    value={description.description}
                    label="Job Description"
                    onChange={handleFieldChange('description')}
                    disabled={disabled}
                    format="markdown"
                />
            </div>
            <div className="job-field-row">
                <MultiSelectField
                    value={description.visa}
                    label="Visa"
                    onChange={handleFieldChange('visa')}
                    options={visaOptions}
                    disabled={disabled}
                />
            </div>
            <div className="job-field-row" css={locationRowStyles}>
                <TextField
                    value={description.location}
                    label="Location"
                    onChange={handleFieldChange('location')}
                    disabled={disabled}
                    className="job-form-location"
                />
                <SelectField
                    value={description.timezone}
                    label="Timezone"
                    onChange={handleFieldChange('timezone')}
                    options={timezoneOptions}
                    disabled={disabled}
                    className="job-form-timezone"
                />
            </div>
            <div className="job-field-row">
                <TextField
                    value={description.minExperienceYears}
                    label="Min Experience Years"
                    onChange={handleFieldChange('minExperienceYears')}
                    disabled={disabled}
                    type="number"
                />
                <TextField
                    value={description.maxExperienceYears}
                    label="Max Experience Years"
                    onChange={handleFieldChange('maxExperienceYears')}
                    disabled={disabled}
                    type="number"
                />
            </div>
            <div className="job-field-row">
                <TextField
                    value={description.compensation}
                    label="Compensation"
                    onChange={handleFieldChange('compensation')}
                    disabled={disabled}
                />
            </div>{' '}
            <div className="job-field-row">
                <TextField
                    value={description.hiringManager}
                    label="Hiring Manager"
                    onChange={handleFieldChange('hiringManager')}
                    disabled={disabled}
                />
            </div>
            <div className="job-field-row">
                <RichTextField
                    value={description.interviewPrep}
                    label="Interview Prep"
                    onChange={handleFieldChange('interviewPrep')}
                    disabled={disabled}
                    format="markdown"
                />
            </div>
            <div className="job-field-row">
                <TextField
                    value={description.linkToJobDescription}
                    label="Link to Job Description"
                    onChange={handleFieldChange('linkToJobDescription')}
                    disabled={disabled}
                />
            </div>
            <div className="job-field-row">
                <SelectField
                    value={description.diversity}
                    label="Diversity"
                    onChange={handleFieldChange('diversity')}
                    options={diversityOptions}
                    disabled={disabled}
                />
            </div>
            <div className="job-field-row">
                <JobOnePager jobId={id} />
            </div>
        </div>
    );
};
